import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useJournalEntries } from 'hooks/api/useAccounts';
import notificationService from 'services/notificationService';
import { voucherTypeService, journalEntryService } from 'api/services';
import type { TransactionDto, JournalEntryQueryParams } from 'api/types';
import { JournalEntryFormModal, JournalEntryViewModal } from '../../../modals/modules/accounts/JournalEntryFormModal';

type ViewMode = 'list' | 'grid';

interface JournalEntryScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString() : '—');
const fmtMoney = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const JournalEntryScreen: React.FC<JournalEntryScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('journal-entry');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [queryParams, setQueryParams] = useState<JournalEntryQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useJournalEntries(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [vtFilter, setVtFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<TransactionDto | null>(null);

  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const [copyInstanceId, setCopyInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handlePageChange = useCallback((idx: number) => setQueryParams(p => ({ ...p, page: idx + 1 })), []);
  const handlePageSizeChange = useCallback((size: number) => setQueryParams(p => ({ ...p, pageSize: size, page: 1 })), []);

  const handleAddNew = useCallback(() => {
    if (formModalOpen) formModalMinimizeRef.current?.();
    let next = 1;
    if (tabId) {
      while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++;
    }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelectedRecord(null); setFormModalOpen(true);
  }, [formModalOpen, tabId, isMinimized]);

  const handleEdit = useCallback((id: string) => {
    const record = data.find(t => t.id === id);
    if (!record) return;
    if (record.isLocked) {
      notificationService.toast.warning('This voucher is locked and cannot be edited.');
      return;
    }
    const modalId = tabId ? `${tabId}-form-modal-edit-${record.transactionNo}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setSelectedRecord(record); setFormMode('edit'); setFormModalOpen(true);
  }, [data, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((id: string) => {
    const record = data.find(t => t.id === id);
    if (!record) return;
    // Auto-minimize the currently open modal so the new Copy draft starts fresh.
    if (formModalOpen) formModalMinimizeRef.current?.();
    let next = 1;
    if (tabId) {
      while (isMinimized(`${tabId}-form-modal-copy-${next}`)) next++;
    }
    setCopyInstanceId(String(next));
    setFormMode('copy'); setSelectedRecord(record); setFormModalOpen(true);
  }, [data, formModalOpen, tabId, isMinimized]);

  const handleDelete = useCallback(async (id: string) => {
    const record = data.find(t => t.id === id);
    if (!record) return;
    if (record.isLocked) {
      notificationService.toast.warning('This voucher is locked and cannot be deleted.');
      return;
    }
    const ok = await confirmDelete({ resourceLabel: record.transactionNo });
    if (!ok) return;
    try {
      await journalEntryService.delete(record.id, record.rowVersion);
      notificationService.toast.success(`Journal ${record.transactionNo} deleted.`);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not delete the journal entry.');
      if (err?.response?.status === 409) refetch();
    }
  }, [data, confirmDelete, refetch]);

  const handleView = useCallback((id: string) => {
    const record = data.find(t => t.id === id);
    if (!record) return;
    const viewId = tabId ? `${tabId}-view-modal-${record.transactionNo}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setSelectedRecord(record); setViewModalOpen(true);
  }, [data, tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreModal = useCallback((mode: 'create' | 'edit' | 'copy', instanceId?: string, transactionNo?: string) => {
    if (mode === 'edit') {
      if (transactionNo) {
        const rec = data.find(t => t.transactionNo === transactionNo);
        if (rec) setSelectedRecord(rec);
      }
      setFormMode('edit');
    } else if (mode === 'copy') {
      if (instanceId) setCopyInstanceId(instanceId);
      if (transactionNo) {
        const rec = data.find(t => t.transactionNo === transactionNo);
        if (rec) setSelectedRecord(rec);
      }
      setFormMode('copy');
    } else {
      if (instanceId) setCreateInstanceId(instanceId);
      setFormMode('create');
    }
    setFormModalOpen(true);
  }, [data]);

  const handleRestoreView = useCallback((transactionNo: string) => {
    const rec = data.find(t => t.transactionNo === transactionNo);
    if (rec) { setSelectedRecord(rec); setViewModalOpen(true); }
  }, [data]);

  const handlePosted = useCallback(() => {
    setFormModalOpen(false); setSelectedRecord(null); setFormMode('create'); refetch();
  }, [refetch]);

  const tableColumns: SyutGridColumn<TransactionDto>[] = useMemo(() => [
    { id: 'transactionNo', header: 'Voucher No', accessorKey: 'transactionNo', size: 150 },
    {
      id: 'transactionDate', header: 'Date', accessorKey: 'transactionDate', size: 110,
      cell: ({ row }) => fmtDate((row.original as TransactionDto).transactionDate),
    },
    {
      id: 'type', header: 'Type', accessorKey: 'voucherTypeName', size: 170,
      cell: ({ row }) => (row.original as TransactionDto).voucherTypeName || (row.original as TransactionDto).voucherClass,
    },
    {
      id: 'reference', header: 'Reference', accessorKey: 'reference', size: 150,
      cell: ({ row }) => (row.original as TransactionDto).reference || '—',
    },
    {
      id: 'narration', header: 'Narration', accessorKey: 'narration', size: 240,
      cell: ({ row }) => (row.original as TransactionDto).narration || '—',
    },
    {
      id: 'amount', header: 'Amount', accessorKey: 'totalBaseAmount', size: 140,
      cell: ({ row }) => {
        const t = row.original as TransactionDto;
        return <span style={{ display: 'block', textAlign: 'right' }}>{fmtMoney(t.totalBaseAmount)} {t.currencyCode}</span>;
      },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'status', size: 150,
      cell: ({ row }) => {
        const t = row.original as TransactionDto;
        return (
          <span style={{ display: 'inline-flex', gap: '0.375rem', alignItems: 'center' }}>
            <span className="badge">{t.status}</span>
            {t.isLocked && (
              <span className="badge" style={{ background: '#6b7280', color: '#fff' }}
                title={t.lockSource || 'Locked'}>
                🔒 Locked
              </span>
            )}
          </span>
        );
      },
    },
  ], []);

  const getRowActions = useCallback((rec: TransactionDto): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(rec.id) },
    ];
    if (can('CREATE')) {
      actions.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(rec.id) });
    }
    if (!rec.isLocked) {
      if (can('UPDATE')) {
        actions.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(rec.id) });
      }
      if (can('DELETE')) {
        actions.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDelete(rec.id), variant: 'danger' });
      }
    }
    return actions;
  }, [can, handleView, handleEdit, handleDelete, handleCopy]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAddNew, 'New Journal Entry'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  // Journal / Contra voucher types for the filter
  const loadVtOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await voucherTypeService.getAll({ pageSize: 100 });
      const journals = res.data.filter(v => ['JOURNAL', 'CONTRA'].includes(v.voucherClass));
      const filtered = input
        ? journals.filter(v => `${v.voucherCode} ${v.voucherName}`.toLowerCase().includes(input.toLowerCase()))
        : journals;
      return [
        { value: '', label: 'All Types' },
        ...filtered.map(v => ({ value: v.id, label: `${v.voucherCode} - ${v.voucherName}` })),
      ];
    } catch {
      return [{ value: '', label: 'All Types' }];
    }
  }, []);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev, voucherTypeId: vtFilter || undefined,
      fromDate: fromDate || undefined, toDate: toDate || undefined, page: 1,
    }));
    setFilterOpen(false);
  }, [vtFilter, fromDate, toDate]);

  const clearFilters = useCallback(() => {
    setVtFilter(''); setFromDate(''); setToDate('');
    setQueryParams(prev => ({ page: 1, pageSize: prev.pageSize }));
  }, []);

  const appliedCount = [vtFilter, fromDate, toDate].filter(Boolean).length;

  return (
    <EnterpriseScreenShell
      title="Journal Entry"
      description="Manual general-ledger vouchers — post adjustments, provisions and reclassifications"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.edit size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search voucher no / reference / narration...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Voucher Type" isAsync loadOptions={loadVtOptions}
                  defaultOptions cacheOptions={false} value={vtFilter}
                  onChange={(v) => setVtFilter((v as string) || '')}
                  placeholder="All Types" isClearable size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={tableColumns} enableRowSelection
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange}
            rowActions={getRowActions}
            emptyMessage={loading ? 'Loading...' : 'No journal entries yet — click Add to post one.'}
            frozenRightColumns={['actions']} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <IconSystem.edit size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  <p>{loading ? 'Loading...' : 'No journal entries yet.'}</p>
                </div>
              ) : (
                data.map((t) => (
                  <div key={t.id} style={{
                    background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                    borderRadius: '0.75rem', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{t.transactionNo}</div>
                        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0.25rem 0 0' }}>
                          {fmtMoney(t.totalBaseAmount)} {t.currencyCode}
                        </h3>
                      </div>
                      <ActionMenu items={getRowActions(t)} />
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {t.voucherTypeName || t.voucherClass} · {fmtDate(t.transactionDate)}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {t.narration || t.reference || '—'}
                    </div>
                  </div>
                ))
              )}
            </div>
            {data.length > 0 && (
              <div className="pagination-footer">
                <div className="pagination-info">Showing {data.length} of {pagination.totalItems} items</div>
                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <select value={queryParams.pageSize ?? 25}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem' }}>
                    {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size} / page</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <JournalEntryFormModal isOpen={formModalOpen} mode={formMode}
        transaction={formMode === 'edit' || formMode === 'copy' ? selectedRecord : undefined}
        onPosted={handlePosted}
        onClose={() => { setFormModalOpen(false); setSelectedRecord(null); setFormMode('create'); }}
        tabId={tabId} moduleName={moduleName}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        onRestoreModal={handleRestoreModal}
        minimizeRef={formModalMinimizeRef} />

      <JournalEntryViewModal isOpen={viewModalOpen} transaction={selectedRecord}
        onClose={() => { setViewModalOpen(false); setSelectedRecord(null); }}
        tabId={tabId} moduleName={moduleName}
        onRestoreView={handleRestoreView} />
    </EnterpriseScreenShell>
  );
};

export default JournalEntryScreen;
